import React from "react";
import PropTypes from "prop-types";
import ButtonComponent from "../../../components/atoms/button";
import { NEXT, PREVIOUS } from "../../../constants/common/common";

/**
 * Pagination component for rendering page navigation.
 *
 * @param {Object} props - Component properties.
 * @param {number} props.noOfPages - Total number of pages.
 * @param {number} [props.page] - Current page (controlled mode).
 * @param {number} [props.defaultPage] - Default page (uncontrolled mode).
 * @param {Function} props.onChange - Callback function to handle page change.
 * @param {Object} [props.customClasses] - Custom CSS classes for styling.
 * @returns {JSX.Element} Pagination component.
 */
const Pagination = ({ noOfPages, page, defaultPage, onChange, customClasses = {}, isMobile }) => {
  const currentPage = page || defaultPage || 1;

  /**
   * Handles the page change when a page button is clicked.
   *
   * @param {number} newPage - The new page number to navigate to.
   */
  const onHandlePageChange = (newPage) => {
    if (newPage !== currentPage && newPage > 0 && newPage <= noOfPages) {
      onChange(newPage);
    }
  };

  /**
   * Renders the page buttons dynamically based on the current page and total pages.
   *
   * @returns {JSX.Element[]} Array of JSX elements for page buttons.
   */
  const renderPageButtons = () => {
    const buttons = [];
    const maxVisiblePages = isMobile ? 1 : 5; // Maximum number of visible page buttons.
    const halfVisible = Math.floor(maxVisiblePages / 2);

    // Calculate the range of pages to display.
    let startPage = Math.max(1, currentPage - halfVisible);
    let endPage = Math.min(noOfPages, currentPage + halfVisible);

    // Adjust range to ensure maxVisiblePages are shown where possible.
    if (currentPage <= halfVisible) {
      endPage = Math.min(noOfPages, maxVisiblePages);
    } else if (currentPage > noOfPages - halfVisible) {
      startPage = Math.max(1, noOfPages - maxVisiblePages + 1);
    }

    // Add first page and ellipsis if applicable.
    if (startPage > 1) {
      buttons.push(
        <ButtonComponent
          key={1}
          onClick={() => onHandlePageChange(1)}
          label="1"
          size="small"
          buttonVariant="outlined"
          classes={`flex min-h-[40px] min-w-[40px] items-center justify-center rounded-none border-b-0 border-l-0 border-r border-t-0 border-r-greySoft bg-white-900 py-4 px-4 text-14  text-fnp-500  ${customClasses.pageButton || ""}`}
        />,
      );
      if (startPage > 2) {
        buttons.push(
          <span
            key="start-ellipsis"
            className={`flex min-h-[40px] min-w-[40px] items-center justify-center rounded-none border-b-0 border-l-0 border-r border-t-0 border-r-greySoft bg-white-900 px-4 text-14 text-fnp-500 ${customClasses.ellipsis || ""}`}
          >
            ...
          </span>,
        );
      }
    }

    // Add buttons for the middle pages.
    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <ButtonComponent
          key={i}
          onClick={() => onHandlePageChange(i)}
          label={String(i)}
          size="small"
          buttonVariant="outlined"
          classes={`flex min-h-[40px] min-w-[40px] items-center justify-center rounded-none border px-4 py-4 text-fnp-500 ${
            i === currentPage
              ? "bg-grey-300 border-none "
              : "border-r-greySoft border-l-0 border-t-0 border-b-0 bg-white-900"
          }  text-14 ${customClasses.pageButton || ""}`}
          aria-current={i === currentPage ? "page" : undefined}
        />,
      );
    }

    // Add last page and ellipsis if applicable.
    if (endPage < noOfPages) {
      if (endPage < noOfPages - 1) {
        buttons.push(
          <span
            key="end-ellipsis"
            className={`flex min-h-[40px] min-w-[40px] items-center justify-center rounded-none border-b-0 border-l-0 border-r border-t-0 border-r-greySoft bg-white-900 px-4 text-14 text-fnp-500  ${customClasses.ellipsis || ""}`}
          >
            ...
          </span>,
        );
      }
      buttons.push(
        <ButtonComponent
          key={noOfPages}
          onClick={() => onHandlePageChange(noOfPages)}
          label={String(noOfPages)}
          size="small"
          buttonVariant="outlined"
          classes={`flex min-h-[40px] min-w-[40px] items-center justify-center rounded-none border-none bg-white-900 py-4 px-4 text-14 text-fnp-500  ${customClasses.pageButton || ""}`}
        />,
      );
    }

    return buttons;
  };

  return (
    <nav className={`flex items-center ${customClasses.container || ""}`} aria-label="Pagination">
      <ButtonComponent
        size="small"
        label="Previous"
        buttonVariant="iconWithTextOutlined"
        icon="/icons/arrow-left-all-gifts.svg"
        iconStyleWidth={20}
        iconStyleHeight={20}
        disabled={currentPage === 1}
        aria-label={PREVIOUS}
        onClick={() => onHandlePageChange(currentPage - 1)}
        classes={`inline-flex min-h-[40px] min-w-[100px] items-center justify-center rounded-none border-b-0 border-l-0 border-r border-t-0 border-r-greySoft bg-white-900 py-4 px-4 disabled:pointer-events-none disabled:opacity-30 ease-in-out duration-300 hover:bg-grey-300  ${customClasses.prevButton || ""}`}
      />
      <div className={`flex items-center ${customClasses.pagesContainer || ""}`}>{renderPageButtons()}</div>

      <ButtonComponent
        size="small"
        label="Next"
        buttonVariant="countryDropdown"
        subIcon="/icons/arrow-right-all-gifts.svg"
        iconStyleWidth={20}
        iconStyleHeight={20}
        aria-label={NEXT}
        disabled={currentPage === noOfPages}
        onClick={() => onHandlePageChange(currentPage + 1)}
        subIconClass={"w-auto"}
        subIconStyleWidth={20}
        subIconStyleHeight={20}
        classes={`inline-flex min-h-[40px] min-w-[70px] items-center justify-center rounded-none border-b-0 border-l border-r-0 border-t-0 border-l-greySoft bg-white-900 py-4 px-4 disabled:pointer-events-none disabled:opacity-30 ease-in-out duration-300 hover:bg-grey-300 ${customClasses.nextButton || ""}`}
      />
    </nav>
  );
};

Pagination.propTypes = {
  noOfPages: PropTypes.number.isRequired,
  page: PropTypes.number,
  defaultPage: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  customClasses: PropTypes.shape({
    container: PropTypes.string,
    pageButton: PropTypes.string,
    prevButton: PropTypes.string,
    nextButton: PropTypes.string,
    pagesContainer: PropTypes.string,
    ellipsis: PropTypes.string,
  }),
  isMobile: PropTypes.bool.isRequired,
};

export default Pagination;
